import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useMemo, useEffect } from 'react';
import { userAPI } from '../../services';
import { User } from '../../types/api';
import { formatPhoneNumber, getLessonTypeLabel } from '../../utils/formatters';
import { getWeekdayLabel } from '../../utils/formatters/labels';
import { PageLoading, EmptyState } from '../../components';
import Tabs from '../../components/Tabs';
import MemberEditModal from '../../components/MemberEditModal';
import Modal from '../../components/Modal';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useIsMobile } from '../../hooks/useIsMobile';

const tabOptions = [
  { value: 'active', label: '활성회원' },
  { value: 'inactive', label: '비활성회원' }
];

const Users: React.FC = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [reactivateUser, setReactivateUser] = useState<User | null>(null);
  const [reactivateData, setReactivateData] = useState({
    lessonDayOfWeek: '',
    lessonStartTime: '',
    lessonEndTime: ''
  });
  const [isReactivating, setIsReactivating] = useState(false);

  const additionalParams = useMemo(
    () => activeTab === 'inactive' ? { includeDeleted: 'true' } : {},
    [activeTab]
  );

  const {
    items: users,
    isLoading,
    isLoadingMore,
    refresh,
    handleScroll
  } = useInfiniteScroll<User>({
    fetchFunction: userAPI.getUsers,
    limit: 200,
    searchTerm,
    statusFilter: activeTab,
    additionalParams
  });

  // 활성회원: 요일+시간 순, 비활성회원: 이름 순
  const sortedUsers = activeTab === 'inactive'
    ? [...users].sort((a, b) => a.name.localeCompare(b.name, 'ko'))
    : users;

  const isMobile = useIsMobile();

  // 데스크탑: 하단 고정 상세 패널
  const [selectedDetail, setSelectedDetail] = useState<User | null>(null);

  useEffect(() => {
    if (isMobile || sortedUsers.length === 0) {
      if (sortedUsers.length === 0) setSelectedDetail(null);
      return;
    }
    setSelectedDetail(prev =>
      prev && sortedUsers.some(u => u.userId === prev.userId)
        ? sortedUsers.find(u => u.userId === prev.userId) || sortedUsers[0]
        : sortedUsers[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, activeTab, isMobile]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    const isConfirmed = window.confirm(
      `정말로 "${userName}" 회원을 삭제하시겠습니까?\n\n주의사항:\n- 해당 회원의 모든 수업 일정이 삭제됩니다\n- 선생님 스케줄에서도 해당 시간대가 비워집니다\n- 이 작업은 되돌릴 수 없습니다\n\n삭제하려면 "확인"을, 취소하려면 "취소"를 클릭하세요.`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      const response = await userAPI.deleteUser(userId);
      if (response.success) {
        alert(`${userName} 회원이 성공적으로 삭제되었습니다.`);
        refresh();
      } else {
        alert(response.message || '회원 삭제에 실패했습니다.');
      }

    } catch {
      alert('회원 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleOpenReactivateModal = (user: User) => {
    setReactivateUser(user);
    setReactivateData({
      lessonDayOfWeek: user.lessonDayOfWeek || '',
      lessonStartTime: user.lessonStartTime?.slice(0, 5) || '',
      lessonEndTime: user.lessonEndTime?.slice(0, 5) || ''
    });
    setIsReactivateModalOpen(true);
  };

  const handleCloseReactivateModal = () => {
    setIsReactivateModalOpen(false);
    setReactivateUser(null);
  };

  const handleReactivate = async () => {
    if (!reactivateUser) return;

    if (!reactivateData.lessonDayOfWeek || !reactivateData.lessonStartTime || !reactivateData.lessonEndTime) {
      alert('요일과 수업시간을 입력해주세요.');
      return;
    }

    setIsReactivating(true);
    try {
      const response = await userAPI.reactivateUser(reactivateUser.userId, reactivateData);
      if (response.success) {
        alert(`${reactivateUser.name} 회원이 다시 활성화되었습니다.`);
        handleCloseReactivateModal();
        refresh();
      } else {
        alert(response.message || '활성화에 실패했습니다.');
      }
    } catch {
      alert('활성화 중 오류가 발생했습니다.');
    } finally {
      setIsReactivating(false);
    }
  };

  const formatLessonTime = (user: User) => {
    if (user.lessonDayOfWeek && user.lessonStartTime && user.lessonEndTime) {
      return `${getWeekdayLabel(user.lessonDayOfWeek)} ${user.lessonStartTime.replace(':00', '')}-${user.lessonEndTime.replace(':00', '')}`;
    }
    return user.assignedTime || 'N/A';
  };

  if (isLoading) {
    return <PageLoading message="회원 목록을 불러오는 중..." fullScreen />;
  }

  return (
    <div className="teacher-page list-detail-page">
      <TeacherPageHeader title="회원관리">
        <Tabs
          options={tabOptions}
          value={activeTab}
          onChange={setActiveTab}
        />
        <input
          className="header-search"
          type="text"
          placeholder="이름·연락처 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </TeacherPageHeader>
      <div className="content-header desktop-hidden">
        <Tabs
          options={tabOptions}
          value={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div className="card-body">
        {sortedUsers.length > 0 ? (
          <div className="requests-container" onScroll={handleScroll}>
            {/* 데스크탑 테이블 뷰 */}
            <div className="table-view">
              <div className="table-wrapper">
                <table className="lesson-requests-table users-table">
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>연락처</th>
                      <th>{activeTab === 'inactive' ? '이전 수업시간' : '수업시간'}</th>
                      <th>{activeTab === 'inactive' ? '비활성일' : '등록일'}</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user) => (
                      <tr
                        key={user.userId}
                        className={`status-row status-${user.status}`}
                        onClick={() => !isMobile && setSelectedDetail(user)}
                      >
                        <td>
                          <div className="name-cell">
                            <span className="name">
                              {user.isForeigner && <span className="badge badge-foreigner">FOR</span>}
                              {user.name}
                            </span>
                            <span className="sub">{getLessonTypeLabel(user.lessonType)}</span>
                          </div>
                        </td>
                        <td>{formatPhoneNumber(user.phoneNumber)}</td>
                        <td className="time-cell">{formatLessonTime(user)}</td>
                        <td>
                          {activeTab === 'inactive' && user.deletedAt
                            ? new Date(user.deletedAt).toLocaleDateString()
                            : new Date(user.registeredAt).toLocaleDateString()
                          }
                        </td>
                        <td>
                          {activeTab === 'active' && user.status === 'active' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleEditUser(user)}
                              >
                                수정
                              </button>
                              <button
                                className="btn-action reject"
                                onClick={() => handleDeleteUser(user.userId, user.name)}
                              >
                                삭제
                              </button>
                            </div>
                          )}
                          {activeTab === 'inactive' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleOpenReactivateModal(user)}
                              >
                                활성화
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모바일 리스트 뷰 */}
            <div className="card-view">
              <div className="requests-list">
                {sortedUsers.map((user) => (
                  <div
                    key={user.userId}
                    className={`request-item status-${user.status}`}
                  >
                    <div className="item-row">
                      <span className="item-name">
                        {user.isForeigner && <span className="badge badge-foreigner">FOR</span>}
                        {user.name}
                      </span>
                      <span className="item-date">
                        {activeTab === 'inactive' && user.deletedAt
                          ? new Date(user.deletedAt).toLocaleDateString()
                          : new Date(user.registeredAt).toLocaleDateString()
                        }
                      </span>
                    </div>
                    <div className="item-row">
                      <span className="item-phone">
                        {user.phoneNumber ? (
                          <a href={`tel:${user.phoneNumber.replace(/[^0-9+]/g, '')}`}>
                            {formatPhoneNumber(user.phoneNumber)}
                          </a>
                        ) : '-'}
                      </span>
                    </div>
                    <div className="item-row">
                      <span className="item-time">{formatLessonTime(user)}</span>
                    </div>
                    <div className="item-actions">
                      {user.phoneNumber && (
                        <a
                          className="btn-call"
                          href={`tel:${user.phoneNumber.replace(/[^0-9+]/g, '')}`}
                        >
                          📞 전화
                        </a>
                      )}
                      {activeTab === 'active' && user.status === 'active' && (
                        <>
                          <button
                            className="btn-approve"
                            onClick={() => handleEditUser(user)}
                          >
                            수정
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => handleDeleteUser(user.userId, user.name)}
                          >
                            삭제
                          </button>
                        </>
                      )}
                      {activeTab === 'inactive' && (
                        <button
                          className="btn-approve"
                          onClick={() => handleOpenReactivateModal(user)}
                        >
                          활성화
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {isLoadingMore && (
              <div className="loading-more">
                <span>더 불러오는 중...</span>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={activeTab === 'inactive' ? '비활성 회원이 없습니다' : '등록된 회원이 없습니다'}
            description={activeTab === 'inactive' ? '비활성화된 회원이 없습니다.' : '회원이 등록되면 여기에 표시됩니다.'}
          />
        )}
      </div>

      {/* 데스크탑 하단 고정 상세 */}
      {selectedDetail && (
        <div className="request-detail-inline table-view">
          <div className="detail-main">
            <div className="detail-title-row">
              <div className="detail-title">회원 상세 — {selectedDetail.name}</div>
              <span className={`status-badge ${selectedDetail.status}`}>
                {selectedDetail.status === 'active' ? '활성' : '비활성'}
              </span>
              {selectedDetail.isForeigner && <span className="badge badge-secondary">Foreign</span>}
              <div className="title-actions">
                {selectedDetail.status === 'active' ? (
                  <>
                    <button className="mini-btn" onClick={() => handleEditUser(selectedDetail)}>
                      정보 수정
                    </button>
                    <button className="mini-btn danger" onClick={() => handleDeleteUser(selectedDetail.userId, selectedDetail.name)}>
                      삭제
                    </button>
                  </>
                ) : (
                  <button className="mini-btn primary" onClick={() => handleOpenReactivateModal(selectedDetail)}>
                    활성화
                  </button>
                )}
              </div>
              <button className="detail-close" onClick={() => setSelectedDetail(null)} aria-label="닫기">✕</button>
            </div>
            <div className="detail-grid">
              <div>
                <div className="dt">연락처</div>
                <div className="dd">{formatPhoneNumber(selectedDetail.phoneNumber)}</div>
              </div>
              <div>
                <div className="dt">수업시간</div>
                <div className="dd">{formatLessonTime(selectedDetail)}</div>
              </div>
              <div>
                <div className="dt">수업 유형</div>
                <div className="dd">{getLessonTypeLabel(selectedDetail.lessonType)}</div>
              </div>
              <div>
                <div className="dt">{selectedDetail.status === 'active' ? '등록일' : '비활성일'}</div>
                <div className="dd">
                  {selectedDetail.status !== 'active' && selectedDetail.deletedAt
                    ? new Date(selectedDetail.deletedAt).toLocaleDateString()
                    : new Date(selectedDetail.registeredAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            {selectedDetail.email && (
              <div className="detail-message">이메일: {selectedDetail.email}</div>
            )}
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      <MemberEditModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        member={selectedUser}
        onSaveSuccess={() => {
          refresh();
          window.dispatchEvent(new Event('refreshPendingCounts'));
        }}
      />

      {/* 재활성화 모달 */}
      <Modal
        isOpen={isReactivateModalOpen}
        onClose={handleCloseReactivateModal}
        title={`${reactivateUser?.name} 회원 재활성화`}
        size="sm"
      >
        <div className="user-edit-form">
          <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
            이전 수업시간이 기본값으로 설정됩니다. 필요시 변경해주세요.
          </p>

          <div className="edit-form-row">
            <label>요일</label>
            <select
              value={reactivateData.lessonDayOfWeek}
              onChange={(e) => setReactivateData({ ...reactivateData, lessonDayOfWeek: e.target.value })}
            >
              <option value="">선택</option>
              <option value="monday">월</option>
              <option value="tuesday">화</option>
              <option value="wednesday">수</option>
              <option value="thursday">목</option>
              <option value="friday">금</option>
              <option value="saturday">토</option>
              <option value="sunday">일</option>
            </select>
          </div>

          <div className="edit-form-row two-col">
            <div className="form-col">
              <label>시작</label>
              <input
                type="time"
                value={reactivateData.lessonStartTime}
                onChange={(e) => setReactivateData({ ...reactivateData, lessonStartTime: e.target.value })}
              />
            </div>
            <div className="form-col">
              <label>종료</label>
              <input
                type="time"
                value={reactivateData.lessonEndTime}
                onChange={(e) => setReactivateData({ ...reactivateData, lessonEndTime: e.target.value })}
              />
            </div>
          </div>

          <div className="edit-form-actions">
            <button
              className="btn btn-secondary"
              onClick={handleCloseReactivateModal}
            >
              취소
            </button>
            <button
              className="btn btn-primary"
              onClick={handleReactivate}
              disabled={isReactivating}
            >
              {isReactivating ? '처리중...' : '활성화'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
